import { useDispatch, useSelector } from "react-redux";
import Hoc from "../../../components/HOC";
import { useEffect, useState, useMemo } from "react";
import { requestEventsAction, requestWorkAnniversaryWishes, setResetStateEvents, setResetStateWorkAnniversaryWishes } from "../../../Redux/EventState/EventActionCreator";
import CustomDataTable from "../../../components/CustomDataTable";
import CustomDropdown from "../../../components/CustomDropdown";
import { MdWork, MdMail, MdClose, MdSave, MdSend, MdAttachFile, MdFormatBold, MdFormatItalic, MdFormatUnderline, MdFormatListBulleted, MdFormatListNumbered, MdFormatQuote, MdInsertEmoticon } from "react-icons/md";
import moment from "moment";
import { toast } from "react-toastify";
import { monthsData } from "../../../Constants";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../../../quill-custom.css';
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import TextInput from "../../../components/TextInput";

// Register custom fonts with Quill
const Font = ReactQuill.Quill.import('formats/font');
Font.whitelist = ['arial', 'times-new-roman', 'georgia', 'verdana', 'courier-new', 'comic-sans-ms', 'impact', 'lucida-console', 'tahoma', 'trebuchet-ms'];
ReactQuill.Quill.register(Font, true);

// Validation schema for the anniversary wishes form
const AnniversaryWishesSchema = Yup.object().shape({
  subject: Yup.string()
    .min(3, 'Subject must be at least 3 characters')
    .max(100, 'Subject must not exceed 100 characters')
    .required('Subject is required'),
  message: Yup.string()
    .min(10, 'Message must be at least 10 characters')
    .required('Message is required'),
  ccEmails: Yup.string()
    .test('valid-emails', 'Invalid email format', function(value) {
      if (!value) return true; // CC is optional
      const emails = value.split(',').map(email => email.trim());
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emails.every(email => emailRegex.test(email));
    })
});

const Anniversaries = () => {
    const dispatch = useDispatch();
    const eventsList = useSelector((state) => state.EventListReducer);
    const [selectedMonth, setSelectedMonth] = useState(moment().format('MMMM'));
    const [showModal, setShowModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [drafts, setDrafts] = useState([]);
    const [showCc, setShowCc] = useState(false);
    const anniversaryWishes = useSelector((state) => state.WorkAnniversaryWishesReducer);

    const quillModules = {
        toolbar: [
            [{ header: [1, 2, false] }],
            [{ 'font': ['arial', 'times-new-roman', 'georgia', 'verdana', 'courier-new', 'comic-sans-ms', 'impact', 'lucida-console', 'tahoma', 'trebuchet-ms'] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ indent: '-1' }, { indent: '+1' }],
            [{ color: [] }, { background: [] }],
            ['link'],
            ['clean']
        ],
    };

    const quillFormats = [
        'header',
        'font',
        'bold', 'italic', 'underline', 'strike', 'blockquote',
        'list', 'bullet', 'indent',
        'color', 'background',
        'link'
    ];

    const selectedMonthOption = monthsData.find(option => option.value === selectedMonth) || monthsData.find(option => option.value === moment().format('MMMM'));

    // Get filtered anniversaries with memoization
    const filteredAnniversaries = useMemo(() => {
        const monthData = eventsList?.response?.content?.[selectedMonth];
        return monthData?.anniversaries || [];
    }, [eventsList?.response?.content, selectedMonth]);

    useEffect(() => {
        dispatch(setResetStateEvents());
        dispatch(requestEventsAction({ 
            page: eventsList?.page, 
            size: eventsList?.size, 
            eventType: "Anniversary", 
            month: selectedMonth 
        }));
    }, []);

    useEffect(() => {
        if (selectedMonth) {
            dispatch(requestEventsAction({ 
                page: 0, 
                size: eventsList?.size, 
                eventType: "Anniversary", 
                month: selectedMonth 
            }));
        }
    }, [selectedMonth]);

    useEffect(() => {
        if (anniversaryWishes?.response && !anniversaryWishes?.loading) {
            toast.success("Anniversary wishes sent successfully");
            dispatch(setResetStateWorkAnniversaryWishes());
            handleCloseModal();
        }else if(anniversaryWishes?.error && !anniversaryWishes?.loading){
            toast.error("Today is not a Anniversary");
            dispatch(setResetStateWorkAnniversaryWishes());
        }
    }, [anniversaryWishes]);    

    const handleMonthChange = (value) => {
        setSelectedMonth(value);
    };

    const handlePageChange = (page) => {
        const currentMonth = eventsList?.response?.content?.[selectedMonth];
        dispatch(requestEventsAction({ 
            page: page, 
            size: eventsList?.size, 
            eventType: "Anniversary", 
            month: selectedMonth 
        }));
    };

    const handleRowsChange = (size) => {
        dispatch(requestEventsAction({ 
            page: 0, 
            size: size, 
            eventType: "Anniversary", 
            month: selectedMonth 
        }));
    };

    const handleSendWishes = (employee) => {
        setSelectedEmployee(employee);
        setShowCc(false);
        setShowModal(true);
    };

    const handleSubmitWishes = (values, { setSubmitting, resetForm }) => {
        if (selectedEmployee) {
            console.log("Selected employee:", selectedEmployee);
            
            // Try multiple possible property names for user ID
            const userId = selectedEmployee.userId || selectedEmployee.id || selectedEmployee.empId || selectedEmployee.employeeId;
            
            if (!userId) {
                toast.error("Unable to find employee ID");
                console.error("No valid userId found in employee object:", selectedEmployee);
                setSubmitting(false);
                return;
            }
            
            const payload = {
                userId: String(userId), // Convert to string to ensure it's not an object
                cc: values.ccEmails || '',
                subject: values.subject,
                message: values.message
            };
        
            dispatch(requestWorkAnniversaryWishes(payload));
            setSubmitting(false);
        } else {
            toast.error("Please fill in all required fields (subject and message)");
            setSubmitting(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedEmployee(null);
        setShowCc(false);
    };

    const handleSaveDraft = (values) => {
        if (selectedEmployee && values.message.trim()) {
            const draft = {
                toEmails: [selectedEmployee.email],
                ccEmails: values.ccEmails ? values.ccEmails.split(',').map(email => email.trim()) : [],
                subject: values.subject,
                wishesText: values.message
            };
            setDrafts([...drafts, draft]);
            toast.success("Draft saved successfully");
            handleCloseModal();
        }
    };

    // Get initial form values
    const getInitialValues = () => ({
        subject: selectedEmployee ? `Happy Anniversary ${selectedEmployee.name}` : '',
        message: selectedEmployee ? 
            `<p>Dear ${selectedEmployee.name},</p><br/><p>Congratulations on your Work Anniversary! 🎉 Thank you for your dedication and valuable contributions to our team. Here's to many more successful years together!</p><br/><p>Best regards,</p>` : '',
        ccEmails: ''
    });

    const columns = [
        {
            name: "Employee Name",
            selector: (row) => row.name || "",
            sortable: true,
            cell: (row) => (
                <div className="py-3 flex items-center">
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 text-primary font-bold">
                        {row.name ? row.name.charAt(0).toUpperCase() : "?"}
                    </div>
                    <p className="font-semibold text-gray-800">{row.name}</p>
                </div>
            )
        },
        {
            name: "Anniversary Date",
            selector: (row) => (row.date ? moment(row.date).format("DD") : ""),
            sortable: true,
            cell: (row) => (
                <div className="text-center">
                    <div className="text-xl ml-2 font-medium">
                        {moment(row.date).format("DD")}
                    </div>
                    <div className="text-sm ml-2 text-gray-500">
                        {moment(row.date).format("MMM")}
                    </div>
                </div>
            ),
        },
        {
            name: "Years of Service",
            selector: (row) => moment().diff(moment(row.date), 'years'),
            sortable: true,
            cell: (row) => (
                <div className="text-center">
                    <div className="text-xl ml-2 font-medium">
                        {moment().diff(moment(row.date), 'years')}
                    </div>
                    <div className="text-sm ml-2 text-gray-500">
                        Years
                    </div>
                </div>
            ),
        },
        {
            name: "Actions",
            selector: (row) => (row.date ? moment(row.date).format("MMM-DD") : ""),
            sortable: true,
            cell: (row) => (
                <button
                    onClick={() => handleSendWishes(row)}
                    className="px-2 py-2 bg-primary text-white rounded-md hover:bg-primaryDark transition-colors flex items-center gap-2"
                >
                    <MdMail size={20} />
                    Send Wishes
                </button>
            ),
        },
    ];

    return (
        <div className="container mx-auto p-4 md:p-6">
            <div className="flex mb-4">
                <div className="w-48 ml-5">
                    <CustomDropdown
                        value={selectedMonthOption}
                        name="month"
                        handleChange={(selectedOption) =>
                            handleMonthChange(selectedOption.value)
                        }
                        options={monthsData}
                        placeholder="Month"
                    />
                </div>
            </div>
            {filteredAnniversaries.length > 0 ? (
                <CustomDataTable
                    columns={columns}
                    data={filteredAnniversaries}
                    serverPagenation
                    paginationTotalRows={eventsList?.response?.content?.[selectedMonth]?.totalAnniversaries || 0}
                    handleChangePage={handlePageChange}
                    handleRowsChange={handleRowsChange}
                    fixedHeaderScrollHeight={"59vh"}
                />
            ) : (
                <div className="flex items-center justify-center h-[60vh] bg-gray-50 rounded-lg">
                    <p className="text-gray-500 text-lg">No anniversaries in {selectedMonth}</p>
                </div>
            )}

            {showModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[calc(100vh-2rem)] flex flex-col relative border border-gray-200 animate-fadeIn">
                        {/* Modal Header */}
                        <div className="sticky top-0 bg-white z-10 border-b border-gray-200 flex items-center justify-between px-6 py-4 gap-4 rounded-t-2xl">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gradient-to-br bg-primary bg-primaryDark rounded-full flex items-center justify-center">
                                    <MdMail className="text-white text-xl" />
                                </div>
                                <div>
                                    <h2 className="text-primary text-2xl font-bold tracking-tight">Send Anniversary Wishes</h2>
                                    <p className="text-sm text-gray-500">Compose your message</p>
                                </div>
                            </div>
                            <button
                                onClick={handleCloseModal}
                                className="text-primary hover:text-primaryDark transition-colors p-2 rounded-full focus:outline-none absolute top-4 right-4"
                                type="button"
                                aria-label="Close"
                            >
                                <MdClose className="text-gray-500 text-xl" />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="overflow-y-auto flex-1" style={{ maxHeight: '70vh' }}>
                            <Formik
                                initialValues={getInitialValues()}
                                validationSchema={AnniversaryWishesSchema}
                                onSubmit={handleSubmitWishes}
                                enableReinitialize
                            >
                                {({ values, errors, touched, handleChange, setFieldValue, isSubmitting }) => (
                                    <Form className="h-full">
                                        {/* Email Fields */}
                                        <div className="px-8 pt-6 pb-2">
                                            {/* To Field */}
                                            <div className="flex items-center space-x-2 py-2 border-b border-gray-200">
                                                <span className="text-sm font-medium text-gray-600 min-w-[40px]">To:</span>
                                                <div className="flex-1">
                                                    <TextInput
                                                        name="toEmails"
                                                        value={selectedEmployee?.email || ''}
                                                        handleChange={() => {}} // Read-only
                                                        placeholder="Recipient email"
                                                        isDisabled={true}
                                                        classes="w-full"
                                                        legend={true}
                                                    />
                                                </div>
                                            </div>

                                            {/* CC Field */}
                                            <div className="mt-2">
                                                {showCc ? (
                                                    <div>
                                                        <div className="flex items-center space-x-2 py-2 border-b border-gray-200">
                                                            <span className="text-sm font-medium text-gray-600 min-w-[40px]">Cc:</span>
                                                            <div className="flex-1">
                                                                <TextInput
                                                                    name="ccEmails"
                                                                    value={values.ccEmails}
                                                                    handleChange={handleChange}
                                                                    placeholder="Add CC recipients (comma separated)"
                                                                    classes="w-full"
                                                                    legend={true}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                        }
                                                                    }}
                                                                />
                                                                {errors.ccEmails && touched.ccEmails && (
                                                                    <div className="text-red-500 text-xs mt-1">{errors.ccEmails}</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                setShowCc(false);
                                                            }}
                                                            className="text-sm text-primary hover:text-primaryDark font-medium mt-1 px-2 py-1 transition-all"
                                                            type="button"
                                                        >
                                                            Hide Cc
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setShowCc(true);
                                                        }}
                                                        className="text-sm bg-primary rounded-md text-white hover:bg-primaryDark font-medium cc-button px-2 py-1 transition-all"
                                                        type="button"
                                                    >
                                                        Cc
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Subject */}
                                        <div className="px-8 pt-2 pb-2">
                                            <div className="flex items-center space-x-2 py-2 border-b border-gray-200">
                                                <span className="text-sm font-medium text-primary min-w-[60px]">Subject:</span>
                                                <div className="flex-1">
                                                    <TextInput
                                                        name="subject"
                                                        value={values.subject}
                                                        handleChange={handleChange}
                                                        placeholder="Subject"
                                                        classes="w-full text-lg font-medium text-gray-800"
                                                        legend={true}
                                                    />
                                                    {errors.subject && touched.subject && (
                                                        <div className="text-red-500 text-xs mt-1">{errors.subject}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Rich Text Editor */}
                                        <div className="px-8 pt-2 pb-4">
                                            <ReactQuill
                                                theme="snow"
                                                value={values.message}
                                                onChange={(content) => setFieldValue('message', content)}
                                                modules={quillModules}
                                                formats={quillFormats}
                                                placeholder="Write your anniversary wishes here..."
                                                className="h-full"
                                                style={{ minHeight: '180px', maxHeight: '250px' }}
                                            />
                                            {errors.message && touched.message && (
                                                <div className="text-red-500 text-xs mt-1">{errors.message}</div>
                                            )}
                                        </div>

                                        {/* Footer Actions */}
                                        <div className="sticky bottom-0 p-6 border-t border-gray-200 bg-gray-50 flex items-center justify-end rounded-b-2xl">
                                            <div className="flex items-center space-x-3">
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleCloseModal();
                                                    }}
                                                    className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-md transition-colors font-medium"
                                                    type="button"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="submit"
                                                    disabled={isSubmitting || anniversaryWishes?.loading}
                                                    className={`px-6 py-2 bg-gradient-to-r from-primary to-primaryDark text-white rounded-md hover:from-primary hover:to-primaryDark transition-all font-medium flex items-center space-x-2 shadow-md ${(isSubmitting || anniversaryWishes?.loading) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    <MdSend className="text-sm" />
                                                    <span>{(isSubmitting || anniversaryWishes?.loading) ? 'Sending...' : 'Send'}</span>
                                                </button>
                                            </div>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Hoc(Anniversaries);





